import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, type FormInstance } from 'antd';
import { AutoComplete, Input } from 'ui';
import { useDebounce } from 'utils/useDebounce';
import { StyledAddressSelector } from './style';

interface VraaAddress {
  address: string;
  typ: number;
  zipCode?: string;
  lksLat?: number;
  lksLong?: number;
}

interface AddressOption {
  value: string;
  label: string;
  lksLat: number;
  lksLong: number;
}

interface Props {
  form: FormInstance;
  name?: string;
  latName?: string;
  longName?: string;
  onChange?: () => void;
}

const BUILDING_ADDRESS_TYPE = 108;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_URL = `${window.runConfig.backendUrl}/api/v2/vraa/adress/address-struct`;

const toOption = (row: VraaAddress): AddressOption | null => {
  if (
    row.typ !== BUILDING_ADDRESS_TYPE ||
    !Number.isFinite(row.lksLat) ||
    !Number.isFinite(row.lksLong)
  ) {
    return null;
  }

  const label = row.zipCode ? `${row.address}, ${row.zipCode}` : row.address;

  return {
    value: label,
    label,
    lksLat: row.lksLat as number,
    lksLong: row.lksLong as number,
  };
};

export const AddressSelector = ({
  form,
  name = 'address',
  latName = 'lks_lat',
  longName = 'lks_long',
  onChange,
}: Props) => {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue.trim(), 300);
  const [options, setOptions] = useState<AddressOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const selectedAddress: string | undefined | null = Form.useWatch(name, form);

  useEffect(() => {
    abortRef.current?.abort();

    if (debouncedSearch.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);

    fetch(`${SEARCH_URL}?search=${encodeURIComponent(debouncedSearch)}`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Address search failed: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const rows: VraaAddress[] = Array.isArray(data)
          ? data
          : data?.data || [];
        setOptions(
          rows
            .map(toOption)
            .filter((option): option is AddressOption => option !== null)
        );
        setIsLoading(false);
      })
      .catch((error: Error) => {
        if (error.name !== 'AbortError') {
          setOptions([]);
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [debouncedSearch]);

  const handleSelect = (_value: string, option: AddressOption) => {
    const location = form.getFieldValue('location');

    form.setFieldsValue({
      [name]: option.value,
      [latName]: option.lksLat,
      [longName]: option.lksLong,
      location: Array.isArray(location) ? [] : null,
      location_radius: null,
    });
    setSearchValue('');
    setOptions([]);
    onChange?.();
  };

  const handleSearch = (text: string) => {
    if (selectedAddress) {
      form.setFieldsValue({ [name]: null, [latName]: null, [longName]: null });
    }
    setSearchValue(text);
  };

  const handleClear = () => {
    const values: Record<string, unknown> = {
      [name]: null,
      [latName]: null,
      [longName]: null,
    };

    const location = form.getFieldValue('location');
    const hasLocation = Array.isArray(location)
      ? location.length > 0
      : !!location;
    if (!hasLocation && !form.getFieldValue('location_radius')) {
      values.distance_radius = null;
    }

    form.setFieldsValue(values);
    setSearchValue('');
    onChange?.();
  };

  const notFoundContent = isLoading
    ? intl.formatMessage({ id: 'general.loading' })
    : debouncedSearch.length >= MIN_SEARCH_LENGTH
      ? intl.formatMessage({ id: 'form.address_not_found' })
      : null;

  return (
    <StyledAddressSelector>
      <div className={'form-title'}>
        {intl.formatMessage({ id: 'form.address_title' })}
      </div>

      <AutoComplete
        className={`drop-down address-autocomplete ${selectedAddress ? 'selected' : ''}`}
        noStyle
        popupClassName={'address-selector-dropdown'}
        size={'small'}
        value={selectedAddress || searchValue}
        title={selectedAddress || undefined}
        options={selectedAddress ? [] : options}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
        notFoundContent={selectedAddress ? null : notFoundContent}
        placeholder={intl.formatMessage({
          id: 'form.select_address_placeholder',
        })}
        allowClear
      />

      <Input name={name} hidden />
      <Input name={latName} hidden />
      <Input name={longName} hidden />
    </StyledAddressSelector>
  );
};
